import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';

interface SearchBoxProps {
  initialValue?: string;
  placeholder?: string;
  variant?: 'header' | 'hero' | 'page';
}

export default function SearchBox({
  initialValue = '',
  placeholder = '搜索影视名称、演员或资讯关键词',
  variant = 'header',
}: SearchBoxProps) {
  const [value, setValue] = useState(initialValue);
  const navigate = useNavigate();

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const keyword = value.trim();
    if (!keyword) return;
    navigate(`/search?q=${encodeURIComponent(keyword)}`);
  };

  if (variant === 'hero') {
    return (
      <form
        onSubmit={handleSubmit}
        className="flex w-full max-w-[640px] items-center gap-2 rounded-full border border-background-200 bg-background-50/95 p-1.5 pl-5"
        role="search"
      >
        <i className="ri-search-line text-lg text-foreground-500" aria-hidden="true"></i>
        <input
          type="search"
          name="keyword"
          value={value}
          onChange={(event) => setValue(event.target.value)}
          placeholder={placeholder}
          className="h-10 flex-1 bg-transparent text-sm text-foreground-950 outline-none placeholder:text-foreground-500"
          aria-label="搜索影视或资讯"
        />
        <button
          type="submit"
          className="whitespace-nowrap rounded-full bg-accent-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-accent-600 cursor-pointer dark:text-foreground-950"
        >
          搜索
        </button>
      </form>
    );
  }

  if (variant === 'page') {
    return (
      <form
        onSubmit={handleSubmit}
        className="flex w-full flex-col gap-3 sm:flex-row sm:items-center"
        role="search"
      >
        <div className="flex flex-1 items-center gap-2 rounded-full border border-background-300 bg-background-50 px-5">
          <i className="ri-search-line text-base text-foreground-500" aria-hidden="true"></i>
          <input
            type="search"
            name="keyword"
            value={value}
            onChange={(event) => setValue(event.target.value)}
            placeholder={placeholder}
            className="h-11 flex-1 bg-transparent text-sm text-foreground-950 outline-none placeholder:text-foreground-500"
            aria-label="搜索影视或资讯"
          />
        </div>
        <button
          type="submit"
          className="whitespace-nowrap rounded-full bg-primary-500 px-7 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer dark:text-foreground-950"
        >
          搜索
        </button>
      </form>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="hidden items-center gap-2 rounded-full border border-background-200 bg-background-100 px-4 py-1.5 transition-colors focus-within:border-primary-300 focus-within:bg-background-50 lg:flex"
      role="search"
    >
      <i className="ri-search-line text-base text-foreground-500" aria-hidden="true"></i>
      <input
        type="search"
        name="keyword"
        value={value}
        onChange={(event) => setValue(event.target.value)}
        placeholder="搜索影视 / 演员"
        className="h-8 w-40 bg-transparent text-sm text-foreground-950 outline-none placeholder:text-foreground-500 xl:w-52"
        aria-label="搜索影视或演员"
      />
      <button
        type="submit"
        className="whitespace-nowrap text-sm font-medium text-primary-600 transition-colors hover:text-primary-700 cursor-pointer"
      >
        搜索
      </button>
    </form>
  );
}